import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { Mail, RefreshCw, ExternalLink } from 'lucide-react';
import { useAuth } from '../../App';
import { MAIL_API, mailAuth, PANEL } from './mailUtils';
import { MailThreadList } from './MailThreadList';
import { MailThreadView } from './MailThreadView';

/** Every thread in the signed-in user's mailbox about one job (WO + its quote number). */
export default function JobMail({ reference }) {
  const { user } = useAuth();
  const [data, setData] = useState(null);
  const [err, setErr] = useState(null);
  const [open, setOpen] = useState(null);
  const [loading, setLoading] = useState(false);

  const load = () => {
    if (!reference) return;
    setLoading(true); setErr(null);
    axios.get(`${MAIL_API}/job/${encodeURIComponent(reference)}`, mailAuth()).then(r => setData(r.data))
      .catch(e => { const d = e.response?.data?.detail; setErr(typeof d === 'object' ? `${d.message} ${d.action || ''}` : d || 'Could not search your mail'); })
      .finally(() => setLoading(false));
  };
  useEffect(load, [reference]);

  return (
    <div className={`${PANEL} overflow-hidden`} data-testid="job-mail">
      <div className="px-4 py-3 border-b border-slate-200/60 flex items-center gap-2 flex-wrap bg-white/40">
        <Mail className="w-4 h-4 text-sky-700" />
        <h3 className="text-sm font-black text-slate-900">Mail about {reference}</h3>
        {data && <span className="text-[11px] text-slate-500" data-testid="job-mail-terms">searching your mailbox for {data.terms.map(t => `“${t}”`).join(', ')}{data.quote && data.wo ? ` · quote ${data.quote} became WO ${data.wo}` : ''}</span>}
        <button onClick={load} className="ml-auto p-1.5 rounded-lg hover:bg-slate-900/5 text-slate-500" title="Refresh" data-testid="job-mail-refresh"><RefreshCw className={`w-3.5 h-3.5 ${loading ? 'animate-spin' : ''}`} /></button>
        {data && <a href={`https://mail.google.com/mail/u/0/#search/${encodeURIComponent(data.q)}`} target="_blank" rel="noreferrer" className="p-1.5 rounded-lg hover:bg-slate-900/5 text-slate-500" title="Same search in Gmail" data-testid="job-mail-gmail"><ExternalLink className="w-3.5 h-3.5" /></a>}
      </div>
      {err && <p className="px-4 py-3 text-sm text-amber-700" data-testid="job-mail-error">{err}</p>}
      {!err && (
        <div className="grid grid-cols-1 xl:grid-cols-[minmax(280px,380px)_1fr]">
          <div className={`max-h-[560px] overflow-y-auto ${open ? 'hidden xl:block' : ''}`}>
            <MailThreadList threads={data?.threads || []} selectedId={open} loading={loading && !data} onSelect={t => setOpen(t.id)} emptyText={`No mail mentioning ${reference} in your mailbox yet`} />
          </div>
          <div className={`${open ? '' : 'hidden xl:flex'} xl:border-l border-slate-200/60 min-h-[200px] max-h-[640px] overflow-hidden flex flex-col`}>
            {open ? <MailThreadView key={open} threadId={open} me={user?.email} onBack={() => setOpen(null)} onChanged={() => {}} />
              : <p className="m-auto text-sm text-slate-500" data-testid="job-mail-pick">Pick a thread</p>}
          </div>
        </div>
      )}
    </div>
  );
}
